import { addHours, differenceInCalendarDays, differenceInDays, format, isAfter, isFuture, parseISO, startOfToday, subHours } from 'date-fns';
import { vi } from 'date-fns/locale'; // Import locale tiếng Việt
export const formatDateTimeVN = (date: Date | string | number, fm = 'dd/MM/yyyy HH:mm:ss') =>
	date ? format(new Date(date), fm) : '';
export const formatDate = (date: Date | string | number, fm = 'dd/MM/yyyy') => (date ? format(new Date(date), fm) : '');
export const formatTime = (date: Date | string | number, fm = 'HH:mm:ss') => (date ? format(new Date(date), fm) : '');

const formatOrderDateForSubmit = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '';

	if (typeof value === 'string') {
		const normalized = value.trim();
		if (!normalized) return '';
		const isoDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
		if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) return normalized;
	}

	if (typeof value === 'object' && value && 'year' in value && 'month' in value && 'day' in value) {
		const dateValue = value as { year: number; month: number; day: number };
		return `${String(dateValue.day).padStart(2, '0')}/${String(dateValue.month).padStart(2, '0')}/${dateValue.year}`;
	}

	const date = value instanceof Date ? value : new Date(value as string | number);
	return Number.isNaN(date.getTime()) ? String(value) : format(date, 'dd/MM/yyyy');
};

export const formatOrderDatesForSubmit = <T extends Record<string, unknown>>(values: T): T => ({
	...values,
	effective_date: formatOrderDateForSubmit(values.effective_date),
	expired_date: formatOrderDateForSubmit(values.expired_date),
	life_ack_date: formatOrderDateForSubmit(values.life_ack_date),
});

export const formatCurrency = (value: any): string => {
	if (value == null || Number.isNaN(value)) return '';
	return Number(value).toLocaleString('vi-VN', {
		style: 'decimal',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
		// style: 'currency',
		// currency: 'VND',
		// minimumFractionDigits: 0,
	});
};

export const formatNumber = (value: any): string => {
	if (value == null || Number.isNaN(value)) return '';
	return Number(value).toLocaleString('vi');
};
/**
 * Định dạng ngày tháng sang "Thứ N, ngày DD.MM.YYYY" nếu ngày đó chưa tới hạn.
 * Nếu ngày đã qua, có thể trả về một định dạng khác hoặc chuỗi rỗng/null.
 *
 * @param {Date | string | number} dateValue Giá trị ngày tháng đầu vào.
 * @returns {string | null} Chuỗi ngày tháng đã định dạng hoặc null nếu đã qua hạn.
 */
export const formatFutureDate = (dateValue: any) => {
	const date = new Date(dateValue);
	if (Number.isNaN(date.getTime())) {
		console.warn('Invalid date value provided:', dateValue);
		return null; // Trả về null hoặc một giá trị mặc định nếu ngày không hợp lệ
	}

	if (isFuture(date)) {
		// Định dạng ngày tháng: EEEE là tên đầy đủ của thứ, dd.MM.yyyy là ngày.tháng.năm
		// Sử dụng locale 'vi' để có tên thứ bằng tiếng Việt
		return format(date, "EEEE, 'ngày' dd.MM.yyyy", { locale: vi });
	}
	// Nếu ngày đã qua, bạn có thể trả về null, chuỗi rỗng, hoặc định dạng khác
	return null;
};
/**
 * Kiểm tra trạng thái của một sự kiện: "Đang diễn ra" hoặc "Đã hết hạn".
 *
 * @param {Date | string | number} startDateValue Giá trị ngày tháng bắt đầu của sự kiện.
 * @returns {{status: string, formattedDate: string | null}} Đối tượng chứa trạng thái và ngày định dạng.
 */
export const getEventStatus = (startDateValue: Date | string | number, showText?: boolean) => {
	const startDate = new Date(startDateValue);
	const now = new Date();
	if (Number.isNaN(startDate.getTime())) {
		console.warn('Lỗi ngày tháng đầu vào:', startDateValue);
		return { status: 'Lỗi ngày', formattedDate: null, color: 'danger' };
	}
	const startThreshold = subHours(startDate, 2);
	const endThreshold = addHours(startDate, 2);
	const formattedStartDate = format(startDate, 'EEEE,HH:mm,dd.MM.yyyy', {
		locale: vi,
	});
	// if (!showText) {
	// 	return {
	// 		status: 'Sắp diễn ra',
	// 		formattedDate: formattedStartDate,
	// 	};
	// }
	if (isAfter(now, endThreshold)) {
		return {
			status: 'Đã kết thúc',
			formattedDate: formattedStartDate,
			color: 'danger',
		};
	}
	if (isAfter(now, startDate)) {
		return {
			status: 'Đang diễn ra',
			formattedDate: formattedStartDate,
			color: 'success',
		};
	}
	if (isAfter(now, startThreshold)) {
		return {
			status: 'Sắp bắt đầu',
			formattedDate: formattedStartDate,
			color: 'secondary',
		};
	}
	return {
		status: 'Sắp diễn ra',
		formattedDate: formattedStartDate,
	};
};

export const formatStorage = (value: number | string | null | undefined): string => {
	if (value === null || value === undefined) return '0';
	const num = Number(value);
	if (Number.isNaN(num)) return '0';

	if (num === 0) return '0';

	if (num < 1) {
		const mb = num * 1024;
		return `${Math.round(mb * 100) / 100}`;
	}

	return `${Math.round(num * 100) / 100}`;
};

/**
 * Chuyển đổi linh hoạt nhiều định dạng ngày tháng (dd/MM/yyyy, yyyy-MM-dd, ISO, timestamp, Date) thành đối tượng Date.
 */
export const parseCustomDate = (val?: string | Date | number | null): Date | null => {
	if (!val) return null;
	if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
	if (typeof val === 'number') {
		const d = new Date(val);
		return Number.isNaN(d.getTime()) ? null : d;
	}
	if (typeof val === 'string') {
		const clean = val.trim();
		if (!clean) return null;

		// Hỗ trợ định dạng dd/MM/yyyy hoặc dd/MM/yyyy HH:mm:ss
		if (clean.includes('/')) {
			const [datePart, timePart] = clean.split(' ');
			const parts = datePart.split('/');
			if (parts.length === 3) {
				const [d, m, y] = parts;
				const [h = 0, min = 0, s = 0] = timePart ? timePart.split(':') : [];
				const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), Number(s));
				if (!Number.isNaN(date.getTime())) return date;
			}
		}

		// Hỗ trợ định dạng yyyy-MM-dd HH:mm:ss
		if (clean.includes(' ') && clean.includes('-')) {
			const isoFormatted = clean.replace(' ', 'T');
			const parsed = parseISO(isoFormatted);
			if (!Number.isNaN(parsed.getTime())) return parsed;
		}

		const parsedIso = parseISO(clean);
		if (!Number.isNaN(parsedIso.getTime())) return parsedIso;

		const direct = new Date(clean);
		if (!Number.isNaN(direct.getTime())) return direct;
	}
	return null;
};

/**
 * Tính số ngày còn lại đến ngày hết hạn so với hôm nay (cho phép trả về số âm nếu đã quá hạn).
 *
 * @param {string | Date | number | null | undefined} expiredDate Ngày hết hạn.
 * @param {boolean} clampToZero Nếu true, nếu số ngày < 0 thì trả về 0 (cho hiển thị UI). Mặc định là false.
 * @returns {number} Số ngày còn lại (âm nếu đã quá hạn).
 */
export const calculateDaysLeft = (
	expiredDate?: string | Date | number | null,
	clampToZero = false,
): number => {
	if (!expiredDate) return 0;
	try {
		const expiry = parseCustomDate(expiredDate);
		if (!expiry) return 0;
		const today = startOfToday();
		const diff = differenceInCalendarDays(expiry, today);
		return clampToZero ? (diff > 0 ? diff : 0) : diff;
	} catch {
		return 0;
	}
};


